import React, { useState, useEffect } from "react";
import { userService } from "../services/api";
import { useTheme } from "../style/theme";
import "./UserProfile.css";
import axios from "axios";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  Button,
  Input,
  Select,
  MenuItem,
  Menu,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import LanguageIcon from "@mui/icons-material/Language";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import AccessibilityIcon from "@mui/icons-material/Accessibility";
import camera from "../assets/cameraaicon2.png";

const UserProfile = () => {
  const { mode } = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [image, setImage] = useState("");

  useEffect(() => {
    fetchUserData();
    const checkIsMobile = () => setIsMobile(window.innerWidth <= 768);
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getProfile();

      if (response.error) {
        setError(response.error.message || "Failed to fetch user data");
      } else {
        setUser(response.data.data.user);
        setEditData(response.data.data.user);
        setImage(response.data.data.user?.profilePicture || "");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch user data");
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      setEditData(user);
      setValidationErrors({});
    }
    setIsEditing(!isEditing);
  };

  const handleInputChange = (field, value) => {
    if (field === "username") {
      const nextUsername = value;
      const derivedEmail = nextUsername ? `${nextUsername}@clikkmail.com` : "";
      setEditData({ ...editData, username: nextUsername, email: derivedEmail });
    } else {
      setEditData({ ...editData, [field]: value });
    }
    if (validationErrors[field]) {
      setValidationErrors({ ...validationErrors, [field]: null });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!editData.firstName || editData.firstName.trim().length < 1) {
      errors.firstName = "First name is required";
    } else if (editData.firstName.length > 50) {
      errors.firstName = "First name must be less than 50 characters";
    }

    if (!editData.lastName || editData.lastName.trim().length < 1) {
      errors.lastName = "Last name is required";
    } else if (editData.lastName.length > 50) {
      errors.lastName = "Last name must be less than 50 characters";
    }

    if (editData.phoneNumber && !/^\d+$/.test(editData.phoneNumber)) {
      errors.phoneNumber = "Phone number must contain only digits";
    }

    if (
      editData.gender &&
      !["male", "female", "other", "prefer-not-to-say"].includes(
        editData.gender
      )
    ) {
      errors.gender = "Please select a valid gender option";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setUpdateLoading(true);
      setError(null);

      const response = await userService.updateUser(user._id, editData);

      if (response.error) {
        setError(response.error.message || "Failed to update user data");
      } else {
        setUser(response.data.data.user);
        setIsEditing(false);
        const successMsg = document.createElement("div");
        successMsg.className = "success-message";
        successMsg.textContent = "Profile updated successfully!";
        document.body.appendChild(successMsg);
        setTimeout(() => {
          document.body.removeChild(successMsg);
        }, 3000);
      }
    } catch (err) {
      setError(err.message || "Failed to update user data");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleteLoading(true);
      setError(null);

      const response = await userService.deleteUser(user._id);

      if (response.error) {
        setError(response.error.message || "Failed to delete account");
      } else {
        localStorage.removeItem("user");
        localStorage.clear();
        document.cookie.split(";").forEach(function (c) {
          document.cookie = c
            .replace(/^ +/, "")
            .replace(
              /=.*/,
              "=;expires=" + new Date().toUTCString() + ";path=/"
            );
        });
        setUser(null);
        window.location.href = "https://clikkle.com";
      }
    } catch (err) {
      setError(err.message || "Failed to delete account");
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not provided";
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);
    setImage(imageUrl);

    const formData = new FormData();
    formData.append("profilePicture", file);
    formData.append("type", "file");
    formData.append("userId", user._id);

    try {
      const response = await axios.post(
        `https://api.files.clikkle.com:4500/api/fs/upload-profile-picture?id=${user?._id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.data.message === "Profile picture uploaded successfully") {
        const updatedUser = {
          ...user,
          profilePicture: response.data?.profilePictureUrl,
        };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        window.location.reload();
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (error && !user) {
    return (
      <div className="profile-container">
        <div className="error-message">
          <h2>Error Loading Profile</h2>
          <p>{error}</p>
          <button onClick={fetchUserData} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <Box p={2} className={mode === "dark" ? "dark-theme" : "light-theme"}>
      {/* Header */}
      <Grid>
        <div className="flex items-center justify-end md:w-full">
          <div className="flex flex-row items-center justify-end gap-4">
            {/* Edit / Save Actions */}
            <div className="profile-actions">
              <button
                onClick={handleEditToggle}
                className={`edit-btn ${isEditing ? "cancel" : "edit"}`}
                disabled={updateLoading}
                style={{
                  marginRight: "10px",
                  padding: "8px 15px",
                  borderRadius: "5px",
                  cursor: "pointer",
                  border: "1px solid #ccc",
                }}
              >
                {isEditing ? "Cancel" : "Edit Profile"}
              </button>
              {isEditing && (
                <button
                  onClick={handleSave}
                  className="save-btn"
                  disabled={updateLoading}
                  style={{
                    padding: "5px 10px",
                    borderRadius: "5px",
                    cursor: "pointer",
                    backgroundColor: "#3B84D9",
                    color: "white",
                    border: "none",
                  }}
                >
                  {updateLoading ? "Saving..." : "Save Changes"}
                </button>
              )}
            </div>
          </div>
        </div>
      </Grid>

      {/* Error Banner */}
      {error && (
        <div
          className="error-banner custom_border border-gray-300 dark:border-[#ffffff3a]"
          style={{ marginTop: "1rem" }}
        >
          <p>{error}</p>
          <button onClick={() => setError(null)} className="close-error">
            ×
          </button>
        </div>
      )}

      {/* Intro Section */}
      <Grid className="mt-[2rem]">
        <p className="text-center mt-3 text-4xl">Personal Info</p>
        <p className="text-center mt-4 mb-4 text-lg">
          Info about you and your preferences across Clikkle services.
        </p>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 3,
            mb: 5,
          }}
        >
          <Grid container spacing={2}>
            {/* Image Grid */}
            <Grid
              item
              xs={12}
              md={4}
              order={{ xs: 1, md: 2 }}
              className="flex justify-center"
            >
              <img
                src="/images/personalinfo.svg"
                className={`${
                  isMobile ? "w-[50%]" : "w-[16.625rem]"
                } m-0 md:-m-10`}
                alt="Personal Info"
              />
            </Grid>

            {/* Text Grid */}
            <Grid item xs={12} md={8} order={{ xs: 2, md: 1 }}>
              <p
                className={`${
                  isMobile ? "md:text-xl" : "text-[1.4rem]"
                } mb-4 font-medium`}
              >
                Your profile info in Clikkle services
              </p>
              <p className={`${isMobile ? "w-full" : "w-[80%]"} text-[1rem]`}>
                Personal info and options to manage it. You can make some of
                this info, like your contact details, visible to others so they
                can reach you easily. You can also see a summary of your
                profiles.
              </p>
            </Grid>
          </Grid>
        </Box>

        {/* Basic Info Section */}
        <Grid>
          <div className="rounded-lg mt-5 border border-gray-300 dark:border-[#ffffff3a]">
            <div className="pt-[15px] pb-[15px]">
              <p className="ml-2.5 mt-5 mb-1.5 md:text-[1.4rem] font-medium">
                Basic Info
              </p>
              <p className="ml-2.5">
                Some info may be visible to other people using Clikkle services.{" "}
                <a href="#">Learn more</a>
              </p>
            </div>

            {/* Profile Picture */}
            <div className="flex justify-between ml-2.5 mr-2.5 mt-6 text-[0.9rem] mb-[8px]">
              <p>Profile Picture</p>
              <p>Add a profile picture to personalize your account</p>
              <div
                className={`relative ${
                  !isMobile ? "w-12" : "w-16"
                } h-12 -top-2.5 rounded-full overflow-hidden border border-gray-300 dark:border-[#ffffff3a]`}
              >
                <img
                  src={image || user?.profilePicture || "/images/user.svg"}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
                <label
                  htmlFor="upload-button"
                  className="absolute top-5 right-0 bottom-0 left-3 bg-black bg-opacity-50 flex cursor-pointer"
                >
                  <CameraAltIcon className="text-white" />
                </label>
                <Input
                  id="upload-button"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
              </div>
            </div>

            {/* Name */}
            <div className="flex justify-between border-t border-gray-300 dark:border-[#ffffff3a] mt-3.75 mb-2.5 pt-[20px] pb-3 text-[0.9rem]">
              <p className="ml-2.5">Name</p>
              <div style={{ flexGrow: 1, textAlign: "center" }}>
                {isEditing ? (
                  <div className="flex flex-col gap-2 items-center">
                    <Input
                      type="text"
                      value={editData.firstName || ""}
                      onChange={(e) =>
                        handleInputChange("firstName", e.target.value)
                      }
                      placeholder="First Name"
                      className={
                        validationErrors.firstName
                          ? "error-input border p-1 rounded"
                          : "border p-1 rounded"
                      }
                    />
                    <Input
                      type="text"
                      value={editData.lastName || ""}
                      onChange={(e) =>
                        handleInputChange("lastName", e.target.value)
                      }
                      placeholder="Last Name"
                      className={
                        validationErrors.lastName
                          ? "error-input border p-1 rounded"
                          : "border p-1 rounded"
                      }
                    />
                    {validationErrors.firstName && (
                      <span className="text-red-500 text-xs">
                        {validationErrors.firstName}
                      </span>
                    )}
                    {validationErrors.lastName && (
                      <span className="text-red-500 text-xs">
                        {validationErrors.lastName}
                      </span>
                    )}
                  </div>
                ) : (
                  <p>
                    {user?.firstName} {user?.lastName}
                  </p>
                )}
              </div>
              <ArrowForwardIosIcon
                className="mr-2.5 w-[15px]"
                style={{ width: "15px" }}
              />
            </div>

            {/* Birthday */}
            <div className="flex justify-between border-t border-gray-300 dark:border-[#ffffff3a] mt-3.75 mb-2.5 pt-[20px] pb-3 text-[0.9rem]">
              <p className="ml-2.5">Birthday</p>
              <div style={{ flexGrow: 1, textAlign: "center" }}>
                {isEditing ? (
                  <Input
                    type="date"
                    value={
                      editData.dob
                        ? new Date(editData.dob).toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => handleInputChange("dob", e.target.value)}
                    className="border border-gray-300 dark:border-[#ffffff3a] p-1 rounded"
                  />
                ) : (
                  <p>{formatDate(user?.dob)}</p>
                )}
              </div>
              <ArrowForwardIosIcon
                className="mr-2.5 w-[15px]"
                style={{ width: "15px" }}
              />
            </div>

            {/* Gender */}
            <div className="flex justify-between border-t border-gray-300 dark:border-[#ffffff3a] mt-3.75 mb-2.5 pt-[20px] pb-3 text-[0.9rem]">
              <p className="ml-2.5">Gender</p>
              <div style={{ flexGrow: 1, textAlign: "center" }}>
                {isEditing ? (
                  <div>
                    <Select
                      value={editData.gender || ""}
                      onChange={(e) =>
                        handleInputChange("gender", e.target.value)
                      }
                      displayEmpty
                      className={
                        validationErrors.gender
                          ? "error-input border p-1 rounded"
                          : "border p-1 rounded"
                      }
                      sx={{ height: 40, width: '175px' }}
                    >
                      <MenuItem value="">
                        <em>Select Gender</em>
                      </MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                      <MenuItem value="prefer-not-to-say">
                        Prefer not to say
                      </MenuItem>
                    </Select>
                    {validationErrors.gender && (
                      <span className="text-red-500 text-xs block">
                        {validationErrors.gender}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="capitalize">{user?.gender || "Not provided"}</p>
                )}
              </div>
              <ArrowForwardIosIcon
                className="mr-2.5 w-[15px]"
                style={{ width: "15px" }}
              />
            </div>
          </div>
        </Grid>

        {/* Contact Info Section */}
        <Grid>
          <div className="rounded-lg mt-5 border border-gray-300 dark:border-[#ffffff3a]">
            <p className="ml-2.5 mt-5 mb-1.5 md:text-[1.4rem] font-medium">
              Contact Info
            </p>

            <div className="flex justify-between mt-6 mb-2.5 pt-[20px] pb-3 text-[0.9rem]">
              <p className="ml-2.5">Email</p>
              <div>
                <p>
                  {user?.email ||
                    (user?.username
                      ? `${user.username}@clikkmail.com`
                      : "Not provided")}
                </p>
                {user?.recoveryEmail && (
                  <p className="text-xs text-gray-500">
                    Recovery: {user.recoveryEmail}
                  </p>
                )}
              </div>
              <ArrowForwardIosIcon
                className="mr-2.5 w-[15px]"
                style={{ width: "15px" }}
              />
            </div>

            <div className="flex justify-between border-t border-gray-300 dark:border-[#ffffff3a] mt-3.75 mb-2.5 pt-[20px] pb-3 text-[0.9rem]">
              <p className="ml-2.5">Phone Number</p>
              <div style={{ flexGrow: 1, textAlign: "center" }}>
                {isEditing ? (
                  <div>
                    <Input
                      type="tel"
                      value={editData.phoneNumber || ""}
                      onChange={(e) =>
                        handleInputChange("phoneNumber", e.target.value)
                      }
                      className={
                        validationErrors.phoneNumber
                          ? "error-input border p-1 rounded"
                          : "border p-1 rounded"
                      }
                      placeholder="Phone Number"
                    />
                    {validationErrors.phoneNumber && (
                      <span className="text-red-500 text-xs block">
                        {validationErrors.phoneNumber}
                      </span>
                    )}
                  </div>
                ) : (
                  <p>{user?.phoneNumber || "Not provided"}</p>
                )}
              </div>
              <ArrowForwardIosIcon
                className="mr-2.5 w-[15px]"
                style={{ width: "15px" }}
              />
            </div>

            <div className="border-t border-gray-300 dark:border-[#ffffff3a] mt-3.75 mb-2.5 pt-3 pb-3 text-[0.9rem]">
              <p className="ml-2.5">More Options</p>
              <div
                className={`ml-2.5 mt-[1rem] border border-gray-300 dark:border-[#ffffff3a] ${
                  isMobile ? "w-[80%]" : "w-[25%]"
                } `}
                style={{
                  paddingTop: "8px",
                  paddingLeft: "16px",
                  paddingBottom: "8px",
                  borderRadius: "13px",
                  display: "flex",
                }}
              >
                <img src="/images/sms.svg" className="h-[22px]" alt="sms" />
                <p className="ml-[5px] text-[16px]">
                  Manage emails from Clikkle
                </p>
              </div>
            </div>
          </div>
        </Grid>

        {/* Address & Preferences Section */}
        <Grid>
          <div className="rounded-lg mt-5 border border-gray-300 dark:border-[#ffffff3a]">
            <p className="ml-2.5 mt-5 mb-1.5 md:text-[1.4rem] font-medium">
              Addresses
            </p>
            <p className="ml-2.5">
              Your residential and workplace locations are utilized to tailor
              your interactions across Clikkle services and to provide you with
              more pertinent advertisements.
            </p>
            <p className="ml-2.5 mt-2">
              Additionally, you have the option to include addresses in your
              Clikkle Profile, with the ability to control whether others can
              view them.
              <span className="text-[#3767B1]">
                Learn more{" "}
                <img
                  src="/images/learnmore.svg"
                  className="h-3 w-3 inline"
                  alt="learn more"
                />
              </span>
            </p>

            <div className="flex justify-between items-center mt-5 mb-2.5 pt-3 pb-3 text-[0.9rem]">
              <p className="ml-2.5 flex-1">Home</p>
              <p className="flex-1 text-center">Not Set</p>
              <div className="flex-1 flex justify-end">
                <ArrowForwardIosIcon
                  className="mr-2.5 w-[10px]"
                  style={{ fontSize: "15px" }}
                />
              </div>
            </div>

            <div className="flex justify-between items-center border-t border-gray-300 dark:border-[#ffffff3a] mt-5 mb-2.5 pt-[20px] pb-3 text-[0.9rem]">
              <p className="ml-2.5 flex-1">Work</p>
              <p className="flex-1 text-center">Not Set</p>
              <div className="flex-1 flex justify-end">
                <ArrowForwardIosIcon
                  className="mr-2.5 w-[10px]"
                  style={{ fontSize: "15px" }}
                />
              </div>
            </div>

            <div className="flex justify-between items-center border-t border-gray-300 dark:border-[#ffffff3a] mt-5 mb-2.5 pt-[20px] pb-3 text-[0.9rem]">
              <p className="ml-2.5 flex-1">Other Addresses</p>
              <p className="flex-1 text-center">None</p>
              <div className="flex-1 flex justify-end">
                <ArrowForwardIosIcon
                  className="mr-2.5 w-[10px]"
                  style={{ fontSize: "15px" }}
                />
              </div>
            </div>
          </div>

          <Grid container className="flex flex-row mt-4" spacing={2}>
            <Grid item xs={12} sm={6}>
              <div className="flex flex-row w-full p-2.5 border-t border-gray-300 dark:border-[#ffffff3a] rounded-t-lg mt-8 border border-gray-300 dark:border-[#ffffff3a]">
                <div className="flex-4 p-2.5 w-full">
                  <p
                    className={`${
                      !isMobile ? "text-[1.4rem]" : "text-xl"
                    } font-medium w-[50%] text-nowrap`}
                  >
                    Your profiles
                  </p>
                  <p className="mt-4 text-[1rem] font-normal mb-[3.3rem]">
                    Observe the appearance of your <br />
                    various profiles across Clikkle services
                  </p>
                </div>
                <div className="flex-3 p-6"></div>
              </div>
              <div className="border-t border-gray-300 dark:border-[#ffffff3a] rounded-b-lg border border-solid">
                <p className="text-[#3767B1] text-base font-medium p-[18px] leading-8">
                  See profiles
                </p>
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div className="flex flex-row w-full p-2.5 border-t border-gray-300 dark:border-[#ffffff3a] rounded-t-lg mt-8 border border-gray-300 dark:border-[#ffffff3a]">
                <div className="flex-4 p-2.5 w-full">
                  <p className="md:text-[1.4rem] font-medium">
                    Choose what others see
                  </p>
                  <p className="mt-4 text-base font-normal">
                    Choose which personal details you want to share with others
                    while using your primary Clikkle Account profile across all
                    Clikkle services.
                  </p>
                </div>
                {!isMobile && (
                  <div className="flex-3 p-6">
                    <div>
                      <img
                        src="/images/readingNote.svg"
                        alt="privacy"
                        className="p-[20px]"
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="border-t border-gray-300 dark:border-[#ffffff3a] rounded-b-lg border border-solid">
                <p className="text-[#3767B1] text-base font-medium p-[18px] leading-8">
                  Manage your data & privacy
                </p>
              </div>
            </Grid>
          </Grid>

          <Grid
            container
            className="flex"
            style={{ marginTop: "60px", justifyContent: "space-between" }}
          >
            <div>
              <p className="md:text-[1.4rem] font-medium">
                Additional details and preferences for <br />
                Clikkle Services
              </p>
              <p className="mt-2">
                Methods for confirming your identity and preferences for web
                settings
              </p>
            </div>
            <div className={`${!isMobile ? "mt-[-45px]" : "mt-[0px]"}`}>
              <img
                src="/images/Todo.svg"
                style={{ width: "350px" }}
                alt="Todo"
              />
            </div>
          </Grid>

          <Grid container className="flex flex-row mt-4" spacing={2}>
            <Grid item xs={12} sm={6}>
              <div
                className="w-full border-t border-gray-300 dark:border-[#ffffff3a] rounded-t-lg mt-8 border border-gray-300 dark:border-[#ffffff3a]"
                style={{ borderRadius: "6px" }}
              >
                <div className="flex-4 p-2.5 w-full mt-2">
                  <p className="text-[1.4rem] font-medium w-[50%]">Password</p>
                  <p className="mt-4 text-[1rem] font-normal mb-10">
                    A secure password helps protect your Clikkle Account
                  </p>
                </div>
                <div
                  className={`flex ${!isMobile ? "mb-[9.9rem]" : "mb-[20px]"}`}
                  style={{ justifyContent: "space-between" }}
                >
                  <div
                    className="flex pl-[10px]"
                    style={{ flexDirection: "column" }}
                  >
                    <p>***********</p>
                    <button
                      type="button"
                      className="text-[#3767B1] text-left hover:underline mt-1"
                      onClick={() =>
                        window.open(
                          "https://accounts.clikkle.com/account-recovery-forgot-password",
                          "_blank"
                        )
                      }
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <div>
                    <ArrowForwardIosIcon
                      className="mr-2.5 w-[10px]"
                      style={{ fontSize: "15px" }}
                    />
                  </div>
                </div>
              </div>
            </Grid>
            <Grid item xs={12} sm={6}>
              <div
                className="w-full border-t border-gray-300 dark:border-[#ffffff3a] rounded-t-lg mt-8 border border-gray-300 dark:border-[#ffffff3a]"
                style={{ borderRadius: "6px" }}
              >
                <div className="flex-4 p-2.5 w-full mt-2">
                  <p className="md:text-[1.4rem] font-medium">
                    General preferences for the web
                  </p>
                  <p className="mt-4 text-base font-normal">
                    Manage settings for Clikkle services from the web.
                  </p>
                </div>
                <div
                  className="flex mt-5 mb-5"
                  style={{ justifyContent: "space-between" }}
                >
                  <div className="flex items-center pl-[10px]">
                    <div className="flex items-center">
                      <LanguageIcon
                        className="mr-2.5 w-[10px]"
                        style={{ fontSize: "25px" }}
                      />
                      <div className="flex flex-col">
                        <p className="text-[1rem]">Language</p>
                        <p className="text-[14px]">English (United States)</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <ArrowForwardIosIcon
                      className="mr-2.5 w-[10px]"
                      style={{ fontSize: "15px" }}
                    />
                  </div>
                </div>
                <div
                  className="flex mt-5 mb-5 border-t border-gray-300 dark:border-[#ffffff3a] pt-[15px]"
                  style={{ justifyContent: "space-between" }}
                >
                  <div className="flex items-center pl-[10px]">
                    <div className="flex items-center">
                      <KeyboardIcon
                        className="mr-2.5 w-[10px]"
                        style={{ fontSize: "25px" }}
                      />
                      <div className="flex flex-col">
                        <p className="text-[1rem]">Input Tools</p>
                        <p className="text-[14px]">English (United States)</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <ArrowForwardIosIcon
                      className="mr-2.5 w-[10px]"
                      style={{ fontSize: "15px" }}
                    />
                  </div>
                </div>
                <div
                  className="flex mt-5 mb-5 border-t border-gray-300 dark:border-[#ffffff3a] pt-[15px]"
                  style={{ justifyContent: "space-between" }}
                >
                  <div className="flex items-center pl-[10px]">
                    <div className="flex items-center">
                      <AccessibilityIcon
                        className="mr-2.5 w-[10px]"
                        style={{ fontSize: "25px" }}
                      />
                      <div className="flex flex-col">
                        <p className="text-[1rem]">Accessibility</p>
                        <p className="text-[14px]">Off</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <ArrowForwardIosIcon
                      className="mr-2.5 w-[10px]"
                      style={{ fontSize: "15px" }}
                    />
                  </div>
                </div>
              </div>
            </Grid>
          </Grid>
        </Grid>

        {/* Danger Zone */}
        <Grid className="mt-8">
          <div className="rounded-lg border border-red-300 bg-red-50 p-6">
            <h2 className="text-red-600 text-xl font-bold mb-4">Danger Zone</h2>
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-bold text-red-800">Delete Account</h3>
                <p className="text-red-700 text-sm">
                  Permanently delete your account and all associated data. This
                  action cannot be undone.
                </p>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
                disabled={deleteLoading}
              >
                Delete Account
              </button>
            </div>
          </div>
        </Grid>
      </Grid>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => {
            setShowDeleteModal(false);
            setDeleteConfirmation("");
          }}
        >
          <div
            className={`rounded-lg p-6 max-w-md w-full mx-4 ${
              mode === "dark" ? "bg-[#141414] text-white" : "bg-white text-black"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Confirm Account Deletion</h2>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation("");
                }}
                className={`text-2xl ${
                  mode === "dark"
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                disabled={deleteLoading}
              >
                ×
              </button>
            </div>
            <div className="mb-6">
              <div className="text-4xl mb-4 text-center">⚠️</div>
              <h3 className="font-bold mb-2">Are you absolutely sure?</h3>
              <p
                className={`text-sm mb-4 ${
                  mode === "dark" ? "text-gray-300" : "text-gray-600"
                }`}
              >
                This action <strong>cannot be undone</strong>. This will
                permanently delete your account and remove all your data from
                our servers.
              </p>
              <ul
                className={`list-disc pl-5 text-sm mb-4 space-y-1 ${
                  mode === "dark" ? "text-gray-300" : "text-gray-600"
                }`}
              >
                <li>
                  All your personal information will be permanently deleted
                </li>
                <li>Your business information and settings will be lost</li>
                <li>You will lose access to all associated services</li>
              </ul>
              <p className="mb-2 text-sm">
                Please type <strong>DELETE</strong> to confirm:
              </p>
              <Input
                type="text"
                placeholder="Type DELETE to confirm"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                className={`w-full border p-2 rounded ${
                  mode === "dark"
                    ? "bg-[#252525] border-[#444] text-white"
                    : ""
                }`}
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation("");
                }}
                className={`px-4 py-2 border rounded ${
                  mode === "dark"
                    ? "hover:bg-[#252525] border-[#444]"
                    : "hover:bg-gray-100"
                }`}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                disabled={deleteConfirmation !== "DELETE" || deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default UserProfile;
